import React, { useState } from 'react'
import { differenceInHours, startOfYear } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { calculatorVar, projectVar } from '../../../apollo/reactive-vars'
import {
  DemandFragment,
  LocalGridFragment,
  ProviderFragment,
} from '../../../generated/graphql'
import {
  EnergyType,
  mapEnergyTypeToDemandType,
} from '../../../calculators/EnergyFlow'
import { ConsumptionType } from '../../system-container/energy-system/usage-matrix-util'
import { Section } from '../../common/Section'
import {
  FormValues,
  UsageMatrixControls,
} from '../../system-container/energy-system/UsageMatrixControls'
import { UsageMatrixTable } from '../../system-container/energy-system/UsageMatrixTable'
import { addDemandsToLocalGridProviders } from '../../../local-grid/local-grid-util'

type Props = {
  localGrid: LocalGridFragment
  providers: ProviderFragment[]
  energyType: EnergyType
  demands: DemandFragment[]
}

export function LocalGridUsageMatrix({
  localGrid,
  providers,
  energyType,
  demands,
}: Props): React.ReactElement {
  const project = useReactiveVar(projectVar)
  const calculator = useReactiveVar(calculatorVar)
  const { t } = useTranslation()

  const startOfYearDate = startOfYear(
    project?.calendarYear
      ? new Date(`${project.calendarYear}-01-01 00:00:00`)
      : new Date(),
  )
  const [controls, setControls] = useState<FormValues>({
    consumptionType: ConsumptionType.TotalEnergy,
    date: startOfYearDate,
  })
  const hourOfYear = differenceInHours(controls.date, startOfYearDate)

  const localGridCalculator = calculator?.findLocalGrid(localGrid.id)

  if (!localGridCalculator) {
    return <div />
  }

  const providersWithDemands = providers.map(
    addDemandsToLocalGridProviders(localGridCalculator.type),
  )

  const demandType = mapEnergyTypeToDemandType(energyType)
  const matrixDemands = demands
    .filter((demand) => demand.type === demandType)
    .map((demand) => ({
      id: demand.id,
      name: demand.hourSeries.name,
      isToggleable: false,
      isEditable: false,
    }))

  return (
    <Section title={t('localGrid.tabs.energySystem.usageMatrix')}>
      <UsageMatrixControls
        startOfYearDate={startOfYearDate}
        defaultValues={controls}
        onSubmit={(values) => setControls(values)}
      />

      <UsageMatrixTable
        energyType={energyType}
        consumptionType={controls.consumptionType}
        hourOfYear={hourOfYear}
        calculator={localGridCalculator}
        providers={providersWithDemands}
        demands={matrixDemands}
      />
    </Section>
  )
}
